/**
 * Operations Permissions Manager
 * Main component for managing user operations permissions
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Search, Shield, Users } from 'lucide-react';
import {
  OPERATIONS_PERMISSIONS,
  useUsersWithPermissions,
} from '../../hooks/useOperationsPermissionManagement';
import { OperationsUserPermissionRow } from './OperationsUserPermissionRow';

/**
 * Debounce hook for search input
 */
function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function OperationsPermissionsManager() {
  const { t } = useTranslation('operations');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data: users, isLoading, error } = useUsersWithPermissions(debouncedSearch);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Shield className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <CardTitle>{t('settings.permissions.title')}</CardTitle>
            <CardDescription>{t('settings.permissions.description')}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={t('settings.permissions.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">{t('settings.permissions.loading')}</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-8 text-red-600">
            {t('settings.permissions.error')}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && users?.length === 0 && (
          <div className="text-center py-8">
            <Users className="h-12 w-12 mx-auto text-gray-300" />
            <p className="mt-2 text-gray-500">{t('settings.permissions.noUsers')}</p>
          </div>
        )}

        {/* Users Table */}
        {!isLoading && !error && users && users.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[250px]">
                    {t('settings.permissions.user')}
                  </TableHead>
                  {OPERATIONS_PERMISSIONS.map((permission) => (
                    <TableHead
                      key={permission.key}
                      className="text-center w-[100px]"
                      title={t(permission.descriptionKey)}
                    >
                      {t(permission.labelKey)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <OperationsUserPermissionRow key={user._id} user={user} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Legend */}
        <div className="pt-4 border-t">
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            {t('settings.permissions.legend')}
          </h4>
          <ul className="space-y-1">
            {OPERATIONS_PERMISSIONS.map((permission) => (
              <li key={permission.key} className="text-sm text-gray-600">
                <span className="font-medium">{t(permission.labelKey)}:</span>{' '}
                {t(permission.descriptionKey)}
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}
